import React, { useState, useEffect } from 'react';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { ROLES, isAdmin } from '../utils/roles';
import { usersService } from '../services';
import PageHeader from '../components/ui/PageHeader';
import ListCard, { ListCards, CardAction } from '../components/ui/ListCard';
import useConfirm from '../components/ui/useConfirm';

const UserManagement = () => {
  const { user: currentUser } = useFirebaseAuth();
  const [confirm, confirmDialog] = useConfirm();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingUser, setEditingUser] = useState(null);

  // Fetch all users
  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await usersService.getAll();
      setUsers(response.users || []);
    } catch (err) {
      // Only log errors to console, don't show to user
      console.error('Error fetching users:', err);
      // Set empty data instead of error
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only fetch users if user is admin
    if (isAdmin(currentUser)) {
      fetchUsers();
    }
  }, [currentUser]);

  // Update user role
  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      await usersService.updateRole(userId, newRole);
      setSuccess('User role updated successfully!');
      setEditingUser(null);
      fetchUsers(); // Refresh user list
    } catch (err) {
      setError(err.message || 'Failed to update user role');
    } finally {
      setLoading(false);
    }
  };

  // Delete user
  const handleDeleteUser = async (userId) => {
    if (!(await confirm('This cannot be undone.', { title: 'Delete this user?', confirmText: 'Delete' }))) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      await usersService.delete(userId);
      setSuccess('User deleted successfully!');
      fetchUsers(); // Refresh user list
    } catch (err) {
      setError(err.message || 'Failed to delete user');
    } finally {
      setLoading(false);
    }
  };

  // Check if current user is admin - moved after hooks
  if (!isAdmin(currentUser)) {
    return (
      <DashboardLayout>
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
            <p className="text-gray-600">Only administrators can access user management.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div>
        <div className="max-w-6xl mx-auto space-y-4 md:space-y-6">
          <PageHeader title="Users" subtitle="Who can use the tracker, and their role" />

          {/* Error and Success Messages */}
          {/* Errors are logged to console only, not displayed to user */}
          {success && (
            <div role="status" className="bg-emerald-50 text-emerald-800 px-4 py-3 rounded-xl text-sm font-medium">
              {success}
            </div>
          )}

          {/* Users List */}
          <div className="md:bg-white md:rounded-lg md:shadow-md md:p-6">
            <h2 className="hidden md:block text-xl font-semibold text-gray-800 mb-6">Manage Users</h2>

            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">Loading users...</p>
              </div>
            ) : (
              <>
              <ListCards>
                {users.length === 0 ? (
                  <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500">No users yet.</div>
                ) : users.map((user) => (
                  <ListCard
                    key={user.id}
                    title={user.name}
                    subtitle={user.email}
                    meta={`${user.email_confirmed_at ? 'Verified' : 'Pending'} · joined ${new Date(user.created_at).toLocaleDateString()}`}
                    badge={
                      <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${user.role === ROLES.ADMIN ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                        {user.role === ROLES.ADMIN ? 'Admin' : 'Sales Rep'}
                      </span>
                    }
                    actions={editingUser === user.id ? (
                      <>
                        <select
                          aria-label="Role"
                          value={user.role}
                          onChange={(e) => handleUpdateUserRole(user.id, e.target.value)}
                          className="h-9 text-sm border border-gray-300 rounded-lg pl-2 pr-8"
                        >
                          <option value={ROLES.SALES_REP}>Sales Representative</option>
                          <option value={ROLES.ADMIN}>Administrator</option>
                        </select>
                        <CardAction onClick={() => setEditingUser(null)}>Done</CardAction>
                      </>
                    ) : (
                      <>
                        <CardAction tone="primary" onClick={() => setEditingUser(user.id)}>Edit role</CardAction>
                        {user.id !== currentUser.id && (
                          <CardAction tone="danger" onClick={() => handleDeleteUser(user.id)}>Delete</CardAction>
                        )}
                      </>
                    )}
                  />
                ))}
              </ListCards>
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {users.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-8 text-center">
                          <div className="text-gray-500">
                            <div className="text-lg font-medium mb-2">No users found</div>
                            <div className="text-sm">Users will appear here once they register.</div>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      users.map((user) => (
                        <tr key={user.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{user.name}</div>
                              <div className="text-sm text-gray-500">{user.email}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {editingUser === user.id ? (
                              <select
                                value={user.role}
                                onChange={(e) => handleUpdateUserRole(user.id, e.target.value)}
                                className="text-sm border border-gray-300 rounded px-2 py-1"
                              >
                                <option value={ROLES.SALES_REP}>Sales Representative</option>
                                <option value={ROLES.ADMIN}>Administrator</option>
                              </select>
                            ) : (
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${user.role === ROLES.ADMIN
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-green-100 text-green-800'
                                }`}>
                                {user.role === ROLES.ADMIN ? 'Admin' : 'Sales Rep'}
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${user.email_confirmed_at
                                ? 'bg-green-100 text-green-800'
                                : 'bg-yellow-100 text-yellow-800'
                              }`}>
                              {user.email_confirmed_at ? 'Verified' : 'Pending'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(user.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              {editingUser === user.id ? (
                                <>
                                  <button
                                    onClick={() => setEditingUser(null)}
                                    className="text-gray-600 hover:text-gray-900"
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    onClick={() => setEditingUser(user.id)}
                                    className="text-blue-600 hover:text-blue-900"
                                  >
                                    Edit Role
                                  </button>
                                  {user.id !== currentUser.id && (
                                    <button
                                      onClick={() => handleDeleteUser(user.id)}
                                      className="text-red-600 hover:text-red-900"
                                    >
                                      Delete
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              </>
            )}
          </div>
        </div>
      </div>
      {confirmDialog}
    </DashboardLayout>
  );
};

export default UserManagement;

